import {
  Columns,
  Box,
  Stack,
  Heading,
  Inline,
  Text,
  Divider,
  Column,
  List,
  IconTick,
  ContentBlock,
  IconPromote,
  Bleed,
} from 'braid-design-system';
import type React from 'react';

import { CustomHeading } from './CustomHeading';
import { GetStartedButton } from './GetStartedButton';

import * as style from './AdSelection.css';

interface AdSelectionProps {
  containerRef: React.MutableRefObject<HTMLElement | null>;
}

export const AdSelection = ({ containerRef }: AdSelectionProps) => (
  <ContentBlock>
    <Box
      id="ad-selection-container"
      ref={containerRef}
      padding="large"
      background="body"
      width="full"
    >
      <Stack space="large">
        <Box textAlign="center">
          <CustomHeading semanticLevel="h2" mobileLevel="1" tabletLevel="2">
            Pricing
          </CustomHeading>
        </Box>
        <Text tone="secondary" align="center">
          Reach record numbers of buyers on our platform with advertising
          packages for every business.
        </Text>

        <Bleed
          horizontal={{ mobile: 'none', tablet: 'none' }}
          right={{ mobile: 'none', tablet: 'large' }}
        >
          <Columns align="center" space="large" collapseBelow="tablet">
            {/* 6 month */}
            <Box
              className={style.cards.white}
              boxShadow="large"
              borderRadius="xlarge"
              padding="large"
              display="flex"
              justifyContent="spaceBetween"
              flexDirection="column"
            >
              <Stack space="gutter">
                <Heading level="3">6 month ad pack</Heading>
                <Text tone="secondary">
                  Test the waters and advertise your business for six months.
                </Text>

                <Inline space="xxsmall" alignY="bottom">
                  <Heading level="1">$399</Heading>
                  <Text tone="secondary" size="small">
                    + GST
                  </Text>
                </Inline>
                <Divider />
                <Columns space="medium">
                  <Column>
                    <Stack space="medium">
                      <List
                        space="medium"
                        type="icon"
                        icon={<IconTick tone="positive" />}
                      >
                        <Text>Pay a fixed price and no commissions</Text>
                        <Text>
                          Unlimited ad editing and automated monthly ad refresh
                        </Text>
                        <Text>
                          Price guide calculator, business sale checklist, buyer
                          screening guide
                        </Text>
                      </List>

                      <List type="icon" icon={<IconPromote tone="info" />}>
                        <Text>
                          Access exclusive seller resources including a value
                          calculator guide
                        </Text>
                      </List>
                    </Stack>
                  </Column>
                </Columns>
              </Stack>

              <Box paddingTop="xlarge">
                <GetStartedButton colour="blue" />
              </Box>
            </Box>

            {/* List until sold */}
            <Box
              className={style.cards.blue}
              boxShadow="large"
              borderRadius="xlarge"
              padding="large"
              display="flex"
              justifyContent="spaceBetween"
              flexDirection="column"
            >
              <Stack space="gutter">
                <Heading level="3">List until sold</Heading>
                <Text tone="secondary">
                  No hassle option - your ad stays on site until it sells.
                </Text>

                <Inline space="xxsmall" alignY="bottom">
                  <Heading level="1">$699</Heading>
                  <Text tone="secondary" size="small">
                    + GST
                  </Text>
                </Inline>
                <Divider />
                <Columns space="medium">
                  <Column>
                    <Stack space="medium">
                      <List space="medium" type="icon" icon={<IconTick />}>
                        <Text>Pay a fixed price and no commissions</Text>
                        <Text>
                          Unlimited ad editing and automated monthly ad refresh
                        </Text>
                        <Text>
                          Price guide calculator, business sale checklist, buyer
                          screening guide
                        </Text>
                      </List>

                      <List type="icon" icon={<IconPromote tone="promote" />}>
                        <Text>
                          Advertise your business until it&apos;s sold
                        </Text>
                        <Text>
                          Access exclusive seller resources including a value
                          calculator guide
                        </Text>
                      </List>
                    </Stack>
                  </Column>
                </Columns>
              </Stack>

              <Box paddingTop="xlarge">
                <GetStartedButton colour="white" />
              </Box>
            </Box>
          </Columns>
        </Bleed>
      </Stack>
    </Box>
  </ContentBlock>
);
